import { useState, useCallback, useRef } from 'react'

import {
  linkTagRealtimeOperation,
  unlinkTagRealtimeOperation,
  LinkTagRealtimeRequest,
  UnlinkTagRealtimeRequest,
  TagOperationError
} from 'api/tags'
import { debounce } from 'lodash'

export interface TagOperationState {
  loading: boolean
  error: string | null
  version?: string
}

export interface TagOperationResult {
  success: boolean
  message?: string
  version?: string
}

interface UseTagOperationsReturn {
  linkTag: (
    tagUuid: string,
    payload: LinkTagRealtimeRequest
  ) => Promise<TagOperationResult>
  unlinkTag: (
    tagUuid: string,
    payload: UnlinkTagRealtimeRequest
  ) => Promise<TagOperationResult>
  linkState: TagOperationState
  unlinkState: TagOperationState
  // Immediate operations (no debouncing)
  linkTagImmediate: (
    tagUuid: string,
    payload: LinkTagRealtimeRequest
  ) => Promise<TagOperationResult>
  unlinkTagImmediate: (
    tagUuid: string,
    payload: UnlinkTagRealtimeRequest
  ) => Promise<TagOperationResult>
}

const useTagOperations = (debounceMs = 300): UseTagOperationsReturn => {
  const [linkState, setLinkState] = useState<TagOperationState>({
    loading: false,
    error: null
  })

  const [unlinkState, setUnlinkState] = useState<TagOperationState>({
    loading: false,
    error: null
  })

  // Track pending operations for optimistic locking
  const pendingOperationsRef = useRef<Map<string, string>>(new Map())

  // Internal function to perform link operation
  const performLinkOperation = useCallback(
    async (
      tagUuid: string,
      payload: LinkTagRealtimeRequest
    ): Promise<TagOperationResult> => {
      const operationKey = `link-${tagUuid}-${payload.taggable_uuid}`

      setLinkState((prev) => ({ ...prev, loading: true, error: null }))

      try {
        // Add current version to payload for optimistic locking
        const enhancedPayload = {
          ...payload,
          version:
            pendingOperationsRef.current.get(operationKey) || payload.version
        }

        const result = await linkTagRealtimeOperation(tagUuid, enhancedPayload)

        // Update version tracking
        if (result.version) {
          pendingOperationsRef.current.set(operationKey, result.version)
        }

        setLinkState({ loading: false, error: null, version: result.version })

        return {
          success: true,
          message: result.message,
          version: result.version
        }
      } catch (error: any) {
        const tagError = error as TagOperationError
        const errorMessage = tagError.message || 'Erro ao vincular tag'

        setLinkState({ loading: false, error: errorMessage })

        // Handle optimistic locking conflicts
        if (tagError.status === 409) {
          console.warn('Optimistic locking conflict detected:', operationKey)
        }

        throw {
          success: false,
          message: errorMessage,
          error: tagError
        }
      }
    },
    []
  )

  // Internal function to perform unlink operation
  const performUnlinkOperation = useCallback(
    async (
      tagUuid: string,
      payload: UnlinkTagRealtimeRequest
    ): Promise<TagOperationResult> => {
      const operationKey = `unlink-${tagUuid}-${payload.taggable_uuid}`

      setUnlinkState((prev) => ({ ...prev, loading: true, error: null }))

      try {
        // Add current version to payload for optimistic locking
        const enhancedPayload = {
          ...payload,
          version:
            pendingOperationsRef.current.get(operationKey) || payload.version
        }

        const result = await unlinkTagRealtimeOperation(
          tagUuid,
          enhancedPayload
        )

        // Update version tracking
        if (result.version) {
          pendingOperationsRef.current.set(operationKey, result.version)
        }

        setUnlinkState({ loading: false, error: null, version: result.version })

        return {
          success: true,
          message: result.message,
          version: result.version
        }
      } catch (error: any) {
        const tagError = error as TagOperationError
        const errorMessage = tagError.message || 'Erro ao desvincular tag'

        setUnlinkState({ loading: false, error: errorMessage })

        // Handle optimistic locking conflicts
        if (tagError.status === 409) {
          console.warn('Optimistic locking conflict detected:', operationKey)
        }

        throw {
          success: false,
          message: errorMessage,
          error: tagError
        }
      }
    },
    []
  )

  // Debounced link operation
  const debouncedLinkTag = useRef(
    debounce(
      async (
        tagUuid: string,
        payload: LinkTagRealtimeRequest,
        resolve: (result: TagOperationResult) => void,
        reject: (error: any) => void
      ) => {
        try {
          const result = await performLinkOperation(tagUuid, payload)
          resolve(result)
        } catch (error) {
          reject(error)
        }
      },
      debounceMs
    )
  ).current

  // Debounced unlink operation
  const debouncedUnlinkTag = useRef(
    debounce(
      async (
        tagUuid: string,
        payload: UnlinkTagRealtimeRequest,
        resolve: (result: TagOperationResult) => void,
        reject: (error: any) => void
      ) => {
        try {
          const result = await performUnlinkOperation(tagUuid, payload)
          resolve(result)
        } catch (error) {
          reject(error)
        }
      },
      debounceMs
    )
  ).current

  // Public API functions
  const linkTag = useCallback(
    (
      tagUuid: string,
      payload: LinkTagRealtimeRequest
    ): Promise<TagOperationResult> => {
      return new Promise((resolve, reject) => {
        debouncedLinkTag(tagUuid, payload, resolve, reject)
      })
    },
    [debouncedLinkTag]
  )

  const unlinkTag = useCallback(
    (
      tagUuid: string,
      payload: UnlinkTagRealtimeRequest
    ): Promise<TagOperationResult> => {
      return new Promise((resolve, reject) => {
        debouncedUnlinkTag(tagUuid, payload, resolve, reject)
      })
    },
    [debouncedUnlinkTag]
  )

  // Immediate operations (no debouncing)
  const linkTagImmediate = useCallback(
    (
      tagUuid: string,
      payload: LinkTagRealtimeRequest
    ): Promise<TagOperationResult> => {
      return performLinkOperation(tagUuid, payload)
    },
    [performLinkOperation]
  )

  const unlinkTagImmediate = useCallback(
    (
      tagUuid: string,
      payload: UnlinkTagRealtimeRequest
    ): Promise<TagOperationResult> => {
      return performUnlinkOperation(tagUuid, payload)
    },
    [performUnlinkOperation]
  )

  return {
    linkTag,
    unlinkTag,
    linkState,
    unlinkState,
    linkTagImmediate,
    unlinkTagImmediate
  }
}

export default useTagOperations
